
import { Component, Vue, Prop, Watch } from 'vue-property-decorator'
import Axios, { CancelTokenSource } from 'axios'
import { dataLayer, storeCode, locale } from "../core/constants";
import { SEARCH_QUERY_STRING } from '../helpers/constants'
import { IProductSearchResultsStandard, IContentSearchResults } from '../helpers/types'
import Mixin from './../helpers/mixin'

const MIN_SEARCH_LENGTH = 3

@Component({
  template: `
    <div class="search-wrap" v-click-outside='() => {hasFocus = false;}'>
      <div ref='searchInput' :class='{active: showResultsDropdown}' class='search-input flex-grow-1'>
        <input v-model='searchInput' @focus='hasFocus = true;' @keydown='hasFocus = true;' :placeholder=searchLabel type='text' />
        <i v-if='!searchInput' class="icons8-search search-input__icon"></i>
        <i v-if='searchInput' :loading='loadingContentSearch || loadingProductSearch' class="icons8-delete search-input__icon" @click='reset'></i>
      </div>

      <transition name='fade'>
        <div class="results" v-if='showResultsDropdown'>
          <div class="results__title" v-if="apiSearchNoResult && apiSearchContentNoResult">{{ noSearchResultsFound }}</div>
          <div class="results__title" v-if="apiError">{{ searchErrorMessage }}</div>
          <div v-if="productSearchResults.length">
            <div class="results__title">{{ searchProductsTitle }}</div>
            <template v-for="item in productSearchResults">
              <a class="results__product" v-bind:href="item.url">
                <img class="results__product--image" v-if="item.primaryImage && item.primaryImage.url" v-bind:src="item.primaryImage.url" />
                <div v-else class="results__product--image-placeholder"></div>
                <div class="results__product--content">
                  <h4>{{ item.seoInfo.title }}</h4>
                  <p v-if="item.variationsTotalCount">{{ item.variationsTotalCount }} {{ variantSetTitle(item.variationsTotalCount ) }}</p>
                  <i class="icons8-chevron-right-2"></i>
                </div>
              </a>
            </template>
            <a class="h5 results__link" @click='search(searchInput)'>{{ allProductsMenuLabel }} <i class="icons8-chevron-right-2"></i></a>
          </div>
          <div class="results__title" v-if="apiErrorContent">{{ searchContentError }}</div>
          <div v-if="contentSearchResults.length">
            <div class="results__title">{{ searchContentTitle }}</div>
              <template v-for="item in contentSearchResults">
                <a class="results__content" v-bind:href="item.url">
                  <h6 class="results__content--title">{{ item.title }} <i class="icons8-chevron-right-2"></i></h6>
                </a>
              </template>
            </div>
          </div>
      </transition>

    </div>
    `
})
export default class SearchBar extends Mixin {
  @Prop({ default: '' }) searchLabel: string
  @Prop({ default: '' }) searchPageUrl: string
  @Prop({ default: '' }) variantTitle: string
  @Prop({ default: '' }) variantsTitle: string
  @Prop({ default: '' }) noSearchResultsFound: string
  @Prop({ default: '' }) searchProductsTitle: string
  @Prop({ default: '' }) searchContentTitle: string
  @Prop({ default: '' }) searchErrorMessage: string
  @Prop({ default: '' }) searchContentError: string
  @Prop({ default: '' }) allProductsMenuLabel: string

  cancelTokenSource: CancelTokenSource | null = null
  searchSuggestionsTimer: any = null
  searchQueryTimer: any = null
  hasFocus = false
  loadingContentSearch = false
  loadingProductSearch = false
  searchInput = ''
  productSearchResults: IProductSearchResultsStandard[] = []
  contentSearchResults: IContentSearchResults[] = []
  apiError = false
  apiErrorContent = false
  apiSearchNoResult = false
  apiSearchContentNoResult = false

  get showResultsDropdown(): boolean {
    return this.hasFocus
        && this.searchInput.length >= MIN_SEARCH_LENGTH
        && !this.loadingContentSearch
        && !this.loadingProductSearch
        && (this.productSearchResults.length > 0 || this.contentSearchResults.length > 0 || this.apiError || this.apiSearchNoResult)
  }

  stripHtml(html: string) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.body.textContent || '';
  }

  variantSetTitle(variants: number): string {
    return variants > 1 ? this.variantsTitle : this.variantTitle
  }

  search(evt: string) {
    const suggestionString = this.stripHtml(evt).replace(/\s+/g, ' ').trim();
    window.location.href = `${this.searchPageUrl}?q=` + encodeURIComponent(suggestionString);
  }

  async searchForProductGroups() {
    if (!this.searchInput) return
    this.loadingProductSearch = true

    try {
      const res = await Axios.get(`/${storeCode}/${locale}/storefrontapi/search/product/${this.searchInput}`, { cancelToken: this.cancelTokenSource!.token })
      this.productSearchResults = res.data.products as IProductSearchResultsStandard[]

      if(this.productSearchResults.length === 0) this.apiSearchNoResult = true

    } catch (error) {
      if (!Axios.isCancel(error)) {
        console.error(error)
        this.apiError = true
      }
    }  finally {
      this.loadingProductSearch = false
    }
  }

  async searchForContent() {
    if (!this.searchInput) return
    this.loadingContentSearch = true

    try {
      const res = await Axios.get(`/${storeCode}/${locale}/storefrontapi/search/content/${this.searchInput}`, { cancelToken: this.cancelTokenSource!.token })
      this.contentSearchResults = res.data as IContentSearchResults[]

      if(this.contentSearchResults.length === 0) this.apiSearchContentNoResult = true


    } catch (error) {
      if (!Axios.isCancel(error)) {
        console.error(error)
        this.apiErrorContent = true
      }
    }  finally {
      this.loadingContentSearch = false
    }
  }

  async getSearchResults(): Promise<void> {
    if (!this.searchInput) return
    this.searchSuggestionsTimer = null

    this.resetApiMessages()

    if (this.cancelTokenSource && (this.loadingProductSearch || this.loadingContentSearch)) this.cancelTokenSource.cancel()
    this.cancelTokenSource = Axios.CancelToken.source()

    if (this.searchInput) {
      this.searchForContent()
      this.searchForProductGroups()
    }
  }

  reset(): void {
    this.searchInput = ''
    window.history.replaceState(null, '', window.location.pathname)

    this.resetApiMessages()
    this.productSearchResults = []
    this.contentSearchResults = []
  }

  resetApiMessages(): void {
    this.apiSearchNoResult = false
    this.apiSearchContentNoResult = false
    this.apiError = false
    this.apiErrorContent = false
  }

  @Watch('searchInput')
  onSearchInputChange(input: string) {
    if (input.length === 0) this.reset()
    else if (input.length >= MIN_SEARCH_LENGTH){
      if (this.searchSuggestionsTimer) clearTimeout(this.searchSuggestionsTimer)
      this.searchSuggestionsTimer = setTimeout(this.getSearchResults, 500)


      if (this.searchQueryTimer) clearTimeout(this.searchQueryTimer)
      this.searchQueryTimer = setTimeout(() => {
        const newRelativePathQuery = window.location.pathname + (`?${SEARCH_QUERY_STRING}=` + this.searchInput)
        window.history.replaceState(null, '', newRelativePathQuery)

        if (!dataLayer) return
        dataLayer.push({
          event : 'search',
          searchTerm : this.searchInput
        });

      }, 1000)
    }
  }
}
